import { useEffect, useRef, useState } from 'react'
// eslint-disable-next-line import/no-extraneous-dependencies
import axios from 'axios'
import { toast } from 'react-toastify'
import {
  getRankingTenants,
  getStatusRollup,
  postRecalcularTenants,
} from '../../services/GestaoTenants/getGestaoTenants'

const FASE_PADRAO: FaseTenant[] = ['EM_FUNCIONAMENTO']
const POLL_INTERVALO_MS = 3000

// Filtro de status da base (Organizacao.ativo). Default 'ativas' (só bases ativas).
export type StatusBase = 'ativas' | 'inativas' | 'todas'
const statusToAtivo = (s: StatusBase): boolean | undefined => {
  if (s === 'ativas') return true
  if (s === 'inativas') return false
  return undefined
}

export function useGestaoTenants() {
  const [ranking, setRanking] = useState<RankingTenantsType | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [dataInicio, setDataInicio] = useState('')
  const [dataFim, setDataFim] = useState('')
  const [fases, setFases] = useState<FaseTenant[]>(FASE_PADRAO)
  const [statusBase, setStatusBase] = useState<StatusBase>('ativas')
  const [recalculando, setRecalculando] = useState(false)

  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const montadoRef = useRef(true)
  // Guarda os filtros correntes para o poll refrescar com o periodo atual (evita fechar sobre valor velho).
  const filtrosRef = useRef({ dataInicio, dataFim, fases, statusBase })
  filtrosRef.current = { dataInicio, dataFim, fases, statusBase }

  useEffect(() => {
    montadoRef.current = true
    return () => {
      montadoRef.current = false
    }
  }, [])

  const fetchRanking = async (source: any) => {
    setIsLoading(true)
    const result = await getRankingTenants(
      source,
      dataInicio,
      dataFim,
      fases,
      statusToAtivo(statusBase)
    )
    if ('message' in result) {
      if (result.message !== 'cancelToken') {
        toast.error(result.message)
      }
    } else {
      setRanking(result)
      // Rollup ja em andamento (job noturno ou outro admin) -> entra em "recalculando"; o efeito de
      // polling assume e faz o auto-refresh ao concluir.
      setRecalculando(result.emAndamento)
    }
    setIsLoading(false)
  }

  useEffect(() => {
    const source = axios.CancelToken.source()
    fetchRanking(source)
    return () => source.cancel('cancelToken')
  }, [JSON.stringify({ dataInicio, dataFim, fases, statusBase })])

  // Refaz o ranking com os filtros correntes (sem loading global) - usado ao fim do recalculo.
  const recarregar = async () => {
    const {
      dataInicio: di,
      dataFim: df,
      fases: fs,
      statusBase: sb,
    } = filtrosRef.current
    const source = axios.CancelToken.source()
    const result = await getRankingTenants(
      source,
      di,
      df,
      fs,
      statusToAtivo(sb)
    )
    if (!('message' in result) && montadoRef.current) {
      setRanking(result)
    }
  }

  const pollStatus = () => {
    pollRef.current = setTimeout(async () => {
      const source = axios.CancelToken.source()
      const result = await getStatusRollup(source)
      if (!montadoRef.current) return
      // Falha transitoria no /status: NAO desiste - reagenda (o rollup pode seguir em curso).
      if ('message' in result) {
        pollStatus()
        return
      }
      if (result.emAndamento) {
        pollStatus()
      } else {
        setRecalculando(false)
        toast.success('Rollup atualizado.')
        recarregar()
      }
    }, POLL_INTERVALO_MS)
  }

  // Ciclo de vida do polling atrelado a "recalculando": cobre tanto o clique manual quanto o rollup
  // ja em andamento detectado no load, e sobrevive a troca de filtro (nao depende do efeito de fetch).
  useEffect(() => {
    if (!recalculando) return undefined
    pollStatus()
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current)
    }
  }, [recalculando])

  const recalcular = async () => {
    if (recalculando) return
    const result = await postRecalcularTenants()
    if ('message' in result) {
      toast.error(result.message)
      return
    }
    // 202 (iniciado) e 409 (ja em andamento) sao ambos estado neutro: entra em "recalculando".
    setRecalculando(true)
    toast.info(result.mensagem)
  }

  return {
    ranking,
    isLoading,
    recalculando,
    fases,
    setFases,
    statusBase,
    setStatusBase,
    onChangeDataInicio: setDataInicio,
    onChangeDataFim: setDataFim,
    recalcular,
  }
}
